import { __ } from "@wordpress/i18n";
import { useBlockProps, InspectorControls, RichText, MediaUpload, MediaUploadCheck } from "@wordpress/block-editor";
import { PanelBody, TextControl, TextareaControl, Notice, Button, Card, CardBody, CardHeader, __experimentalSpacer as Spacer } from "@wordpress/components";
import { useState } from "@wordpress/element";

export default function Edit({ attributes, setAttributes }) {
  const blockProps = useBlockProps({ className: "service-explanation" });
  
  const { section1Title, section1Content, section2Title, tabs = [] } = attributes;
  const [expandedTab, setExpandedTab] = useState(null);

  // Helper function to update a specific tab
  const updateTab = (index, field, value) => {
    const newTabs = [...tabs];
    newTabs[index] = { ...newTabs[index], [field]: value };
    setAttributes({ tabs: newTabs });
  };

  // Add a new tab
  const addTab = () => {
    const newTab = {
      id: `tab-${Date.now()}`,
      title: 'New Tab',
      description: 'Add your tab description here...',
      imageUrl: '',
      imageId: 0,
      linkText: 'Learn More',
      linkUrl: '#'
    };
    setAttributes({ tabs: [...tabs, newTab] });
    setExpandedTab(tabs.length); // Expand the new tab
  };

  // Remove a tab
  const removeTab = (index) => {
    const newTabs = tabs.filter((_, i) => i !== index);
    setAttributes({ tabs: newTabs });
    if (expandedTab === index) setExpandedTab(null);
  };

  // Move tab up
  const moveTabUp = (index) => {
    if (index === 0) return;
    const newTabs = [...tabs];
    [newTabs[index - 1], newTabs[index]] = [newTabs[index], newTabs[index - 1]];
    setAttributes({ tabs: newTabs });
  };

  // Move tab down
  const moveTabDown = (index) => {
    if (index === tabs.length - 1) return;
    const newTabs = [...tabs];
    [newTabs[index], newTabs[index + 1]] = [newTabs[index + 1], newTabs[index]];
    setAttributes({ tabs: newTabs });
  };

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Content Settings', 'puroclean')} initialOpen={true}>
          <Notice status="info" isDismissible={false}>
            <p>
              <strong>Note:</strong> This block allows you to manually configure service tabs with custom content.
              <br />
              <strong>Setup:</strong> Add tabs below and customize their titles, descriptions, images and links.
              <br />
              <strong>Display:</strong> Tabs will be shown in an interactive layout on the frontend.
            </p>
          </Notice>
          
          <TextControl
            label={__('Section 1 Title', 'puroclean')}
            value={section1Title}
            onChange={(value) => setAttributes({ section1Title: value })}
          />
          <TextareaControl
            label={__('Section 1 Content', 'puroclean')}
            value={section1Content}
            onChange={(value) => setAttributes({ section1Content: value })}
            rows={8}
            help={__('This content appears above the tabs section', 'puroclean')}
          />
          <TextControl
            label={__('Section 2 Title', 'puroclean')}
            value={section2Title}
            onChange={(value) => setAttributes({ section2Title: value })}
            help={__('Title for the interactive tabs section', 'puroclean')}
          />
        </PanelBody>

        <PanelBody title={__('Tabs Management', 'puroclean')} initialOpen={false}>
          <div style={{ marginBottom: '16px' }}>
            <Button 
              variant="primary" 
              onClick={addTab}
              style={{ width: '100%' }}
            >
              {__('Add New Tab', 'puroclean')}
            </Button>
          </div>

          {tabs.length === 0 && (
            <Notice status="info" isDismissible={false}>
              <p>{__('No tabs configured. Click "Add New Tab" to get started.', 'puroclean')}</p>
            </Notice>
          )}

          {tabs.map((tab, index) => (
            <Card key={tab.id || index} style={{ marginBottom: '16px' }}>
              <CardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <strong>{tab.title || `Tab ${index + 1}`}</strong>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {index > 0 && (
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => moveTabUp(index)}
                        label={__('Move Up', 'puroclean')}
                      >
                        ↑
                      </Button>
                    )}
                    {index < tabs.length - 1 && (
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => moveTabDown(index)}
                        label={__('Move Down', 'puroclean')}
                      >
                        ↓
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => setExpandedTab(expandedTab === index ? null : index)}
                    >
                      {expandedTab === index ? __('Collapse', 'puroclean') : __('Edit', 'puroclean')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      isDestructive
                      onClick={() => removeTab(index)}
                    >
                      {__('Remove', 'puroclean')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {expandedTab === index && (
                <CardBody>
                  <TextControl
                    label={__('Tab Title', 'puroclean')}
                    value={tab.title}
                    onChange={(value) => updateTab(index, 'title', value)}
                  />
                  
                  <Spacer marginY={3} />
                  
                  <TextareaControl
                    label={__('Tab Description', 'puroclean')}
                    value={tab.description}
                    onChange={(value) => updateTab(index, 'description', value)}
                    rows={4}
                  />
                  
                  <Spacer marginY={3} />
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      {__('Tab Image', 'puroclean')}
                    </label>
                    <MediaUploadCheck>
                      <MediaUpload
                        onSelect={(media) => {
                          updateTab(index, 'imageId', media.id);
                          updateTab(index, 'imageUrl', media.url);
                        }}
                        allowedTypes={['image']}
                        value={tab.imageId}
                        render={({ open }) => (
                          <div>
                            {tab.imageId ? (
                              <div>
                                <img 
                                  src={tab.imageUrl} 
                                  alt={tab.title}
                                  style={{ maxWidth: '200px', height: 'auto', marginBottom: '8px', borderRadius: '4px' }}
                                />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <Button variant="secondary" onClick={open}>
                                    {__('Replace Image', 'puroclean')}
                                  </Button>
                                  <Button 
                                    variant="tertiary" 
                                    isDestructive
                                    onClick={() => {
                                      updateTab(index, 'imageId', 0);
                                      updateTab(index, 'imageUrl', '');
                                    }}
                                  >
                                    {__('Remove Image', 'puroclean')}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button variant="secondary" onClick={open}>
                                {__('Select Image', 'puroclean')}
                              </Button>
                            )}
                          </div>
                        )}
                      />
                    </MediaUploadCheck>
                  </div>
                  
                  <Spacer marginY={3} />
                  
                  <TextControl
                    label={__('Link Text', 'puroclean')}
                    value={tab.linkText}
                    onChange={(value) => updateTab(index, 'linkText', value)}
                    placeholder={__('Learn More', 'puroclean')}
                  />
                  
                  <Spacer marginY={3} />
                  
                  <TextControl
                    label={__('Link URL', 'puroclean')}
                    value={tab.linkUrl}
                    onChange={(value) => updateTab(index, 'linkUrl', value)}
                    placeholder={__('#', 'puroclean')}
                    help={__('URL for the tab button link', 'puroclean')}
                  />
                </CardBody>
              )}
            </Card>
          ))}
        </PanelBody>


      </InspectorControls>

      <div {...blockProps}>
        <div className="container max-w-content mx-auto px-4 py-12">
          <RichText
            tagName="h2"
            placeholder={__('Section 1 title...', 'puroclean')}
            value={section1Title}
            onChange={(value) => setAttributes({ section1Title: value })}
            className="text-3xl font-bold mb-6"
          />
          <RichText
            tagName="div"
            multiline="p"
            placeholder={__('Section 1 content...', 'puroclean')}
            value={section1Content}
            onChange={(value) => setAttributes({ section1Content: value })}
            className="text-lg leading-relaxed"
          />
        </div>

        <div className="container max-w-content mx-auto px-4 py-12">
          <RichText
            tagName="h2"
            placeholder={__('Section 2 title...', 'puroclean')}
            value={section2Title}
            onChange={(value) => setAttributes({ section2Title: value })}
            className="text-3xl font-bold mb-8"
          />

          <div style={{
            background: '#FFFFFF',
            boxShadow: '0px 0px 20px rgba(0,0,0,0.06)',
            borderRadius: '10px',
            padding: '32px'
          }}>
            {tabs.length > 0 ? (
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-1/3">
                  <div style={{
                    background: '#F6F6F6',
                    borderRadius: '10px',
                    padding: '24px'
                  }}>
                    <h3 className="text-xl font-semibold mb-4">{__('Our Services', 'puroclean')}</h3>
                    <div className="space-y-2">
                      {tabs.map((tab, index) => (
                        <div 
                          key={tab.id || index}
                          className={`w-full text-left p-3 rounded-lg border-2 ${
                            index === 0 
                              ? 'bg-blue-100 border-blue-500 text-blue-700' 
                              : 'bg-white border-transparent'
                          }`}
                        >
                          {tab.title || `Tab ${index + 1}`}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="lg:w-2/3">
                  {tabs[0] && (
                    <div>
                      <h3 className="text-2xl font-semibold mb-6">{tabs[0].title}</h3>
                      <div className="flex gap-6">
                        {tabs[0].imageId && (
                          <div className="flex-shrink-0">
                            <img 
                              src={tabs[0].imageUrl}
                              alt={tabs[0].title}
                              className="w-48 h-32 object-cover rounded-lg"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="text-lg leading-relaxed text-gray-700 mb-6">
                            <p>{tabs[0].description}</p>
                          </div>
                          <a
                            href="#"
                            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold"
                            onClick={(e) => e.preventDefault()}
                          >
                            {tabs[0].linkText || __('Learn More', 'puroclean')}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold mb-4 text-gray-600">{__('No Tabs Configured', 'puroclean')}</h3>
                <p className="text-gray-500 mb-4">{__('Add tabs using the block settings panel to display interactive content.', 'puroclean')}</p>
                <Button variant="primary" onClick={addTab}>
                  {__('Add Your First Tab', 'puroclean')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}